// The layout of the ecosystem graph: pure, synchronous and deterministic.
// The response comes in; out come the two half-axes of each ring, the
// position of each node and the edges in the shape the canvas takes. The
// same response always gives the same positions.
//
// A ring is an ellipse, wider than tall: a node's name is written level
// beneath its glyph, so nodes side by side need more room than nodes above
// one another. The nodes of a ring are packed round it, each as near the one
// before as their names allow, in the smallest ring that holds them all; what
// room is left over is shared among the gaps. A circle wide enough for the
// names along its top would be so tall that the fitted view shrinks the names
// past reading.
//
// The outer ring is ordered by the inner ring: the nodes an inner node links
// in sit side by side, and the inner node stands at the middle of them, so
// that its lines fan out to them and do not cross the drawing. Where the
// rings are not linked (a single ring, or no line between the two) the nodes
// keep the order the response lists them in: by lane, so that a ring's
// parts, variants or makers stand together.

import {
  Ecosystem,
  EcosystemEdge,
  EcosystemLaneCount,
  EcosystemNode,
  EcosystemNodeType,
} from "../../../types/analytics";

export interface NodeSize {
  width: number;
  height: number;
}

// The glyph each type of node is drawn as: a small box that holds the
// node's count. A node sits on its ring by the centre of its glyph.
export const GLYPH_SIZES: Record<EcosystemNodeType, NodeSize> = {
  product: { width: 36, height: 28 },
  organization: { width: 32, height: 32 },
  project: { width: 36, height: 30 },
  kind: { width: 46, height: 34 },
};

// The width a name wraps within, beneath its glyph
export const LABEL_WIDTH = 130;
// Room for the name on two lines and the line beneath it
const LABEL_HEIGHT = 48;

const footprint = (type: EcosystemNodeType): NodeSize => ({
  width: LABEL_WIDTH,
  height: GLYPH_SIZES[type].height + LABEL_HEIGHT,
});

// The room each type of node takes: its glyph and the name beneath it.
// Neighbours are held apart by this, not by the glyph.
export const NODE_SIZES: Record<EcosystemNodeType, NodeSize> = {
  product: footprint("product"),
  organization: footprint("organization"),
  project: footprint("project"),
  kind: footprint("kind"),
};

// The room a node keeps clear of its neighbours on a ring: its name's width
// and the height of the tallest glyph with a name beneath, each with a gap
const SLOT: NodeSize = {
  width: LABEL_WIDTH + 14,
  height: GLYPH_SIZES.kind.height + LABEL_HEIGHT + 8,
};
// How much wider than tall a ring is
export const RING_ASPECT = 1.75;
// The least height of ring 1 above the centre: clear of the centre's name
const MIN_RING_ONE = 124;
// The least a ring stands above the ring inside it: a name's height clear
const MIN_RING_STEP = 104;
// The step (radians) a node is walked round its ring by
const WALK = 0.004;
const TURN = 2 * Math.PI;
// The top of a ring, where its caption sits
const TOP = -Math.PI / 2;

// The half-axes of a ring: half its width and half its height
export interface RingAxes {
  rx: number;
  ry: number;
}

export interface PlacedEcosystemNode {
  node: EcosystemNode;
  // The centre of the node
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PlacedEcosystemRing {
  ring: number;
  // Half its width and half its height
  rx: number;
  ry: number;
  shown: number;
  omitted: number;
  // What the ring holds, by lane, as the response counts it
  lanes: EcosystemLaneCount[];
}

// A line with its two ends as drawn: every edge between the same two nodes
// is drawn as one line, which stands for all of them
export interface PlacedEcosystemEdge {
  id: string;
  // The edges between the two nodes, in the order of the response
  edges: EcosystemEdge[];
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface EcosystemLayout {
  nodes: PlacedEcosystemNode[];
  rings: PlacedEcosystemRing[];
  edges: PlacedEcosystemEdge[];
}

interface Point {
  x: number;
  y: number;
}

const axesOf = (ry: number): RingAxes => ({ rx: ry * RING_ASPECT, ry });

// The point of a ring at an angle from its top, clockwise
const pointAt = (axes: RingAxes, angle: number): Point => ({
  x: axes.rx * Math.cos(TOP + angle),
  y: axes.ry * Math.sin(TOP + angle),
});

// Whether two nodes are too near for their names: nearer than a slot both
// across and down
const clash = (a: Point, b: Point): boolean =>
  Math.abs(a.x - b.x) < SLOT.width && Math.abs(a.y - b.y) < SLOT.height;

// Whether any two neighbours of a ring clash, the last and the first too
const anyClash = (axes: RingAxes, angles: number[]): boolean =>
  angles.length > 1 &&
  angles.some((angle, index) =>
    clash(
      pointAt(axes, angle),
      pointAt(axes, angles[(index + 1) % angles.length]),
    ),
  );

// Where the first node of a ring sits: half a slot past the top, which
// stays clear for the ring's caption
const startOf = (axes: RingAxes): number =>
  Math.asin(Math.min(1, SLOT.width / 2 / axes.rx));

// The nearest angle past `from` at which a node is clear of the node at
// `from`; null when a whole turn finds none
const nextClear = (axes: RingAxes, from: number): number | null => {
  let angle = from + WALK;
  while (clash(pointAt(axes, from), pointAt(axes, angle))) {
    angle += WALK;
    if (angle - from >= TURN) {
      return null;
    }
  }
  return angle;
};

// The angles of `count` nodes packed round a ring, each as near the one
// before as their names allow; null when the ring is too small for them
const packed = (axes: RingAxes, count: number): number[] | null => {
  const angles = [startOf(axes)];
  for (let index = 1; index < count; index += 1) {
    const angle = nextClear(axes, angles[index - 1]);
    if (angle === null || angle - angles[0] >= TURN) {
      return null;
    }
    angles.push(angle);
  }
  return anyClash(axes, angles) ? null : angles;
};

// The packed angles with the room left over shared among the gaps, the one
// that closes the turn too: every gap grows by the same share. Kept only
// where no name then clashes.
const spread = (axes: RingAxes, angles: number[]): number[] => {
  if (angles.length < 2) {
    return angles;
  }
  const start = angles[0];
  // Where one more node would sit: the turn the packed nodes use
  const beyond = nextClear(axes, angles[angles.length - 1]);
  const used = (beyond ?? start + TURN) - start;
  if (used >= TURN) {
    return angles;
  }
  const grown = angles.map((angle) => start + ((angle - start) * TURN) / used);
  return anyClash(axes, grown) ? angles : grown;
};

// The smallest ring, no lower than `floor`, that holds `count` nodes
const fitted = (floor: number, count: number): RingAxes => {
  if (packed(axesOf(floor), count)) {
    return axesOf(floor);
  }
  let low = floor;
  let high = floor;
  while (!packed(axesOf(high), count)) {
    low = high;
    high *= 1.3;
  }
  for (let step = 0; step < 14; step += 1) {
    const middle = (low + high) / 2;
    if (packed(axesOf(middle), count)) {
      high = middle;
    } else {
      low = middle;
    }
  }
  return axesOf(high);
};

// The half-axes of each ring from the centre out: each at least its least
// height, grown as far as the nodes it holds need
export const ringAxes = (eco: Ecosystem): Map<number, RingAxes> => {
  const axes = new Map<number, RingAxes>([[0, { rx: 0, ry: 0 }]]);
  const rings = Array.from(new Set(eco.nodes.map((node) => node.ring)))
    .filter((ring) => ring > 0)
    .sort((a, b) => a - b);
  let previous = 0;
  for (const ring of rings) {
    const held = eco.nodes.filter((node) => node.ring === ring).length;
    const floor = previous === 0 ? MIN_RING_ONE : previous + MIN_RING_STEP;
    const found = fitted(floor, held);
    axes.set(ring, found);
    previous = found.ry;
  }
  return axes;
};

// The nodes each node is linked to, whichever way the edge runs
const neighboursOf = (eco: Ecosystem): Map<string, Set<string>> => {
  const linked = new Map<string, Set<string>>();
  const add = (from: string, to: string) => {
    linked.set(from, (linked.get(from) ?? new Set<string>()).add(to));
  };
  for (const edge of eco.edges) {
    add(edge.source, edge.target);
    add(edge.target, edge.source);
  }
  return linked;
};

// The outer ring in the order it is drawn in, and the inner node each of its
// nodes hangs off: the first inner node, in the inner ring's order, that it
// is linked to. The nodes of one inner node sit side by side, in the inner
// ring's order; within them, a node is followed by the nodes of its group it
// is itself linked to. A node linked to no inner node comes last.
export const groupedByInner = (
  eco: Ecosystem,
  inner: EcosystemNode[],
  outer: EcosystemNode[],
): { order: EcosystemNode[]; parent: Map<string, string> } => {
  const linked = neighboursOf(eco);
  const parent = new Map<string, string>();
  for (const node of outer) {
    const found = inner.find((candidate) => linked.get(node.id)?.has(candidate.id));
    if (found) {
      parent.set(node.id, found.id);
    }
  }
  const order: EcosystemNode[] = [];
  const taken = new Set<string>();
  const take = (node: EcosystemNode, group: EcosystemNode[]) => {
    if (taken.has(node.id)) {
      return;
    }
    taken.add(node.id);
    order.push(node);
    for (const next of group) {
      if (linked.get(node.id)?.has(next.id)) {
        take(next, group);
      }
    }
  };
  for (const head of [...inner.map((node) => node.id), undefined]) {
    const group = outer.filter((node) => parent.get(node.id) === head);
    group.forEach((node) => take(node, group));
  }
  return { order, parent };
};

// The share of its sector the nodes of a group are spread over; what is left
// keeps the groups apart
const SECTOR_FILL = 0.86;
// The widest a fan is spread, where its nodes need no more: half a turn, so
// that no line of it crosses to the far side of the centre
const FAN_MOST = TURN / 2;

// The angles of `count` nodes within the sector that starts at `from` and is
// `room` wide: packed as near as their names allow, spread over most of the
// sector and set in its middle. Null when the ring does not hold them.
const filled = (
  axes: RingAxes,
  from: number,
  room: number,
  count: number,
): number[] | null => {
  // Packed from `start`, each as near the one before as their names allow
  const pack = (start: number): number[] | null => {
    const angles = [start];
    for (let index = 1; index < count; index += 1) {
      const next = nextClear(axes, angles[index - 1]);
      if (next === null) {
        return null;
      }
      angles.push(next);
    }
    return angles;
  };
  // How near two nodes may stand depends on where on the ring they are: the
  // nodes are packed where they will stand, in the middle of the sector
  let tight = pack(from);
  for (let round = 0; tight && round < 3; round += 1) {
    tight = pack(from + (room - (tight[count - 1] - tight[0])) / 2);
  }
  if (!tight) {
    return null;
  }
  const span = tight[count - 1] - tight[0];
  // Where one more node would sit: the turn the packed nodes use
  const used = (nextClear(axes, tight[count - 1]) ?? tight[0] + TURN) - tight[0];
  // Spread over most of the sector, but no wider than a fan may be; never
  // nearer than packed
  const scale = Math.max(used, Math.min(room * SECTOR_FILL, FAN_MOST)) / used;
  const middle = tight[0] + span / 2;
  const grown = tight.map((angle) => from + room / 2 + (angle - middle) * scale);
  const clashes = grown.some(
    (angle, index) =>
      index > 0 && clash(pointAt(axes, grown[index - 1]), pointAt(axes, angle)),
  );
  return clashes ? tight : grown;
};

// An angle within one turn from the top
const withinTurn = (angle: number): number => ((angle % TURN) + TURN) % TURN;

// The outer ring in sectors: each inner node that links outer nodes in has a
// share of the turn by their number, in the inner ring's order from the top,
// and its outer nodes fan out in the middle of it. An inner node that links
// none in has no share: it stands in an opening between two fans. Out come
// the angle of every outer node and the angle each inner node is wanted at:
// the middle of its fan, or its place in an opening. Null when an outer node
// hangs off no inner node, or the fans do not come clear of one another: the
// outer ring is then packed evenly, as a ring of its own.
const sectored = (
  axes: RingAxes,
  inner: EcosystemNode[],
  order: EcosystemNode[],
  parent: Map<string, string>,
): { outer: Map<string, number>; wanted: Map<string, number> } | null => {
  if (order.length === 0 || order.some((node) => !parent.has(node.id))) {
    return null;
  }
  const outer = new Map<string, number>();
  const wanted = new Map<string, number>();
  // The first and the last angle of each fan, round the ring
  const fans: { first: number; last: number }[] = [];
  let from = 0;
  for (const node of inner) {
    const group = order.filter((child) => parent.get(child.id) === node.id);
    if (group.length === 0) {
      continue;
    }
    const room = (TURN * group.length) / order.length;
    const angles = filled(axes, from, room, group.length);
    if (!angles) {
      return null;
    }
    group.forEach((child, place) => outer.set(child.id, angles[place]));
    wanted.set(node.id, angles.reduce((sum, angle) => sum + angle, 0) / angles.length);
    fans.push({ first: angles[0], last: angles[angles.length - 1] });
    from += room;
  }
  if (anyClash(axes, order.map((node) => outer.get(node.id) ?? 0))) {
    return null;
  }
  // The openings between the fans, and the inner nodes each takes: one more
  // goes to the opening that has the most room for it
  const openings = fans.map((fan, index) => {
    const next = fans[(index + 1) % fans.length];
    const until = index + 1 < fans.length ? next.first : next.first + TURN;
    return { from: fan.last, width: until - fan.last, held: [] as string[] };
  });
  for (const node of inner.filter((candidate) => !wanted.has(candidate.id))) {
    const roomiest = openings.reduce((best, opening) =>
      opening.width / (opening.held.length + 1) > best.width / (best.held.length + 1)
        ? opening
        : best,
    );
    roomiest.held.push(node.id);
  }
  for (const opening of openings) {
    opening.held.forEach((id, place) => {
      wanted.set(
        id,
        withinTurn(opening.from + (opening.width * (place + 1)) / (opening.held.length + 1)),
      );
    });
  }
  return { outer, wanted };
};

// The step (radians) two inner nodes that stand too near are moved apart by,
// and the rounds they are given to come clear
const NUDGE = 0.01;
const NUDGE_ROUNDS = 600;

// How far apart two angles are, the short way round
const apart = (a: number, b: number): number => {
  const turn = Math.abs(a - b) % TURN;
  return Math.min(turn, TURN - turn);
};

// The angles of the inner ring's nodes, each as near as its neighbours allow
// to the angle it is wanted at: the middle of the outer nodes it links in.
// A node that links none in takes the middle of the widest gap left. Where
// the ring is too full for its nodes to come clear of one another there, they
// take the places of the ring packed evenly, in the same order round it and
// turned so that they stand as near the angles they are wanted at as those
// places allow. Null when no node is wanted anywhere.
const followed = (
  axes: RingAxes,
  inner: EcosystemNode[],
  wanted: Map<string, number>,
): Map<string, number> | null => {
  const seats = inner
    .filter((node) => wanted.has(node.id))
    .map((node) => ({ id: node.id, angle: wanted.get(node.id) ?? 0 }))
    .sort((a, b) => a.angle - b.angle);
  if (seats.length === 0) {
    return null;
  }
  for (const node of inner.filter((candidate) => !wanted.has(candidate.id))) {
    let widest = 0;
    let gap = -1;
    seats.forEach((seat, index) => {
      const next = seats[(index + 1) % seats.length];
      const room = index + 1 < seats.length ? next.angle - seat.angle : next.angle + TURN - seat.angle;
      if (room > gap) {
        gap = room;
        widest = index;
      }
    });
    seats.splice(widest + 1, 0, { id: node.id, angle: seats[widest].angle + gap / 2 });
  }
  // Where each node is wanted, before any is moved
  const aims = seats.map((seat) => seat.angle);
  const tooNear = (index: number): boolean => {
    const here = seats[index];
    const next = seats[(index + 1) % seats.length];
    const turn = index + 1 < seats.length ? 0 : TURN;
    return (
      next.angle + turn - here.angle <= 0 ||
      clash(pointAt(axes, here.angle), pointAt(axes, next.angle))
    );
  };
  for (let round = 0; seats.length > 1 && round < NUDGE_ROUNDS; round += 1) {
    let moved = false;
    for (let index = 0; index < seats.length; index += 1) {
      if (tooNear(index)) {
        seats[index].angle -= NUDGE / 2;
        seats[(index + 1) % seats.length].angle += NUDGE / 2;
        moved = true;
      }
    }
    if (!moved) {
      break;
    }
  }
  if (seats.length > 1 && seats.some((_seat, index) => tooNear(index))) {
    const places = spread(axes, packed(axes, seats.length) ?? []);
    if (places.length !== seats.length) {
      return null;
    }
    // The turn of the places that leaves the nodes the least off their aims
    let best = 0;
    let least = Infinity;
    for (let turn = 0; turn < places.length; turn += 1) {
      const off = aims.reduce(
        (sum, aim, index) => sum + apart(places[(index + turn) % places.length], aim) ** 2,
        0,
      );
      if (off < least) {
        least = off;
        best = turn;
      }
    }
    return new Map(
      seats.map((seat, index) => [seat.id, places[(index + best) % places.length]]),
    );
  }
  return new Map(seats.map((seat) => [seat.id, seat.angle]));
};

// Where every node sits: the centre at the origin, the nodes of a ring
// packed round it. The outer ring goes by the inner ring's nodes (see
// `groupedByInner`), and the inner ring's nodes stand at the middle of what
// they link in.
const place = (eco: Ecosystem, axes: Map<number, RingAxes>): Map<string, Point> => {
  const placed = new Map<string, Point>();
  const rings = new Map<number, EcosystemNode[]>();
  for (const node of eco.nodes) {
    if (node.ring === 0) {
      placed.set(node.id, { x: 0, y: 0 });
    } else {
      rings.set(node.ring, [...(rings.get(node.ring) ?? []), node]);
    }
  }
  // The angles of a ring's nodes in the order given, packed round it
  const anglesOf = (ring: number, held: EcosystemNode[]): Map<string, number> => {
    const found = axes.get(ring);
    const angles = found ? spread(found, packed(found, held.length) ?? []) : [];
    return new Map(
      held.map((node, index) => [
        node.id,
        // A ring too small to pack, which `ringAxes` never gives, is spaced
        // evenly instead
        angles[index] ?? (index * TURN) / held.length,
      ]),
    );
  };
  const seat = (ring: number, angles: Map<string, number>) => {
    const found = axes.get(ring);
    if (found) {
      angles.forEach((angle, id) => placed.set(id, pointAt(found, angle)));
    }
  };

  const inner = rings.get(1) ?? [];
  const outer = rings.get(2) ?? [];
  const { order, parent } = groupedByInner(eco, inner, outer);
  const outerAxes = axes.get(2);
  const sectors = outerAxes ? sectored(outerAxes, inner, order, parent) : null;
  const outerAngles = sectors?.outer ?? anglesOf(2, order);
  // Without sectors: the middle of the outer nodes each inner node links in
  const wanted = sectors?.wanted ?? new Map<string, number>();
  if (!sectors) {
    for (const node of inner) {
      const angles = order
        .filter((child) => parent.get(child.id) === node.id)
        .map((child) => outerAngles.get(child.id) ?? 0);
      if (angles.length > 0) {
        wanted.set(node.id, angles.reduce((sum, angle) => sum + angle, 0) / angles.length);
      }
    }
  }
  const innerAxes = axes.get(1);
  seat(1, (innerAxes && followed(innerAxes, inner, wanted)) || anglesOf(1, inner));
  seat(2, outerAngles);
  rings.forEach((held, ring) => {
    if (ring > 2) {
      seat(ring, anglesOf(ring, held));
    }
  });
  return placed;
};

// Rounded to a tenth of a pixel, so that positions compare stably
const round = (value: number): number => Math.round(value * 10) / 10;

// One line per pair of nodes, in the order the pair is first met, from the
// first edge's source to its target
const placeEdges = (
  edges: EcosystemEdge[],
  at: Map<string, { x: number; y: number }>,
): PlacedEcosystemEdge[] => {
  const byPair = new Map<string, EcosystemEdge[]>();
  for (const edge of edges) {
    if (!at.has(edge.source) || !at.has(edge.target)) {
      continue;
    }
    const key = [edge.source, edge.target].sort().join("|");
    byPair.set(key, [...(byPair.get(key) ?? []), edge]);
  }
  return Array.from(byPair.entries()).map(([key, held]) => {
    const from = at.get(held[0].source)!;
    const to = at.get(held[0].target)!;
    return {
      id: key,
      edges: held,
      x1: round(from.x),
      y1: round(from.y),
      x2: round(to.x),
      y2: round(to.y),
    };
  });
};

export const layoutEcosystem = (eco: Ecosystem): EcosystemLayout => {
  const axes = ringAxes(eco);
  const points = place(eco, axes);
  const nodes = eco.nodes.map((node): PlacedEcosystemNode => {
    const found = points.get(node.id);
    const { width, height } = NODE_SIZES[node.type];
    return {
      node,
      x: round(found?.x ?? 0),
      y: round(found?.y ?? 0),
      width,
      height,
    };
  });

  const counts = new Map(eco.rings.map((ring) => [ring.ring, ring]));
  const rings: PlacedEcosystemRing[] = Array.from(axes.entries())
    .filter(([ring]) => ring > 0)
    .map(([ring, found]) => ({
      ring,
      rx: round(found.rx),
      ry: round(found.ry),
      shown: counts.get(ring)?.shown ?? 0,
      omitted: counts.get(ring)?.omitted ?? 0,
      lanes: counts.get(ring)?.lanes ?? [],
    }));

  const at = new Map(nodes.map((placed) => [placed.node.id, placed]));
  return { nodes, rings, edges: placeEdges(eco.edges, at) };
};

// What a node in focus lights: its own lines (`direct`), and the routes by
// which it reaches the centre (`route`), each line stepping one ring
// inwards, through every inner node it links to. `nodes` holds every node
// at the far end of a lit line.
export interface FocusRoutes {
  direct: Set<string>;
  route: Set<string>;
  nodes: Set<string>;
}

export const focusRoutes = (layout: EcosystemLayout, focusId: string | null): FocusRoutes => {
  const lit: FocusRoutes = { direct: new Set(), route: new Set(), nodes: new Set() };
  if (focusId === null) {
    return lit;
  }
  const ringOf = new Map(layout.nodes.map((placed) => [placed.node.id, placed.node.ring]));
  for (const { id, edges } of layout.edges) {
    const edge = edges[0];
    if (edge.source === focusId || edge.target === focusId) {
      lit.direct.add(id);
      lit.nodes.add(edge.source === focusId ? edge.target : edge.source);
    }
  }
  let frontier = [focusId];
  const reached = new Set(frontier);
  while (frontier.length > 0) {
    const next: string[] = [];
    for (const from of frontier) {
      const ring = ringOf.get(from) ?? 0;
      for (const { id, edges } of layout.edges) {
        const edge = edges[0];
        const other =
          edge.source === from ? edge.target : edge.target === from ? edge.source : null;
        if (other === null || (ringOf.get(other) ?? 0) >= ring) {
          continue;
        }
        if (!lit.direct.has(id)) {
          lit.route.add(id);
        }
        lit.nodes.add(other);
        if (!reached.has(other)) {
          reached.add(other);
          next.push(other);
        }
      }
    }
    frontier = next;
  }
  return lit;
};
